import type { TaskDetail } from '@shared/types/tasks';
import { loaderStore } from '@/hooks/loader-store';
import { getTask } from '@/lib/tasks-api';
import { prefetchTaskMeta } from '@/lib/task-meta';

/**
 * Whole tasks loaded ahead of being opened (SF-T-236: the Inbox loads its first task, and the ones
 * either side of the open one), kept in the loader store per project, so signing out drops them and
 * `invalidateLoaders(projectId + ':tasks')` reaches them. Opening a cached task shows it at once
 * (useTaskDetail), and it's still fetched again behind it, so a change since shows up by itself.
 */

/** A task as the server sent it, and when that request started (for the pending-change overlay). */
export type CachedTaskDetail = { task: TaskDetail; fetchedFrom: number };

/** How long a loaded task counts as fresh: no new request to load it ahead again before then. */
export const TASK_DETAIL_PREFETCH_STALE_MS = 30_000;

/**
 * Opening a task whose copy landed less than this long ago shows it without asking again (stepping
 * quickly through the Inbox); an older copy is shown and fetched again behind it. Live changes still
 * load it again either way.
 */
export const TASK_DETAIL_OPEN_FRESH_MS = 10_000;

export function taskDetailKey(projectId: string, taskId: string): string {
  return `${projectId}:tasks:detail:${taskId}`;
}

/** Whether the cached copy of a task landed within `maxAgeMs`. */
export function taskDetailIsFresh(projectId: string | undefined, taskId: string, maxAgeMs = TASK_DETAIL_OPEN_FRESH_MS, now = Date.now()): boolean {
  if (!projectId) return false;
  const snap = loaderStore.snapshot<CachedTaskDetail>(taskDetailKey(projectId, taskId));
  return snap.data?.task.id === taskId && snap.updatedAt !== undefined && now - snap.updatedAt < maxAgeMs;
}

/** The task is gone (404): drop its copy, so it isn't shown as if it still existed. */
export function forgetTaskDetail(projectId: string | undefined, taskId: string): void {
  if (projectId) loaderStore.forget([taskDetailKey(projectId, taskId)]);
}

/** A fresh answer from opening the task: kept, so moving back to it later is instant too. */
export function rememberTaskDetail(projectId: string | undefined, detail: CachedTaskDetail): void {
  if (projectId) loaderStore.receive(taskDetailKey(projectId, detail.task.id), detail);
}

/** Load a task (and its project's columns, labels and people) before it's opened, unless it's fresh. */
export function prefetchTaskDetail(projectId: string, taskId: string): void {
  prefetchTaskMeta(projectId);
  loaderStore.prefetch<CachedTaskDetail>(
    taskDetailKey(projectId, taskId),
    async (signal) => {
      const fetchedFrom = Date.now();
      const res = await getTask(taskId, { project: projectId, signal });
      if (!res.data) throw new Error(res.error ?? 'Could not load the task');
      return { task: res.data.task, fetchedFrom };
    },
    { staleMs: TASK_DETAIL_PREFETCH_STALE_MS }
  );
}
